import { analytics } from "@/lib/analytics";
import { logAuthTransition, withSupabaseAuthRetry } from "@/lib/supabaseAuth";
import { resolveActiveSupabase } from "@/lib/supabaseConfig";
import * as Sentry from "@sentry/react";
import {
  type Session,
  type SupabaseClient,
  type User,
  createClient,
} from "@supabase/supabase-js";
import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Supabase configuration — env-driven so a region cutover is an env change + redeploy,
// not a code edit. VITE_ACTIVE_SUPABASE selects the primary/standby project; values are
// inlined at build time, so they must be set before `vite build`.
export const { url: supabaseUrl, anonKey: supabaseAnonKey } =
  resolveActiveSupabase();

const supabase: SupabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
  auth: { flowType: "implicit" },
});

function setObservabilityUser(user: User | null) {
  if (user) {
    Sentry.setUser({ id: user.id, email: user.email });
    analytics.identify(user.id, { email: user.email });
  } else {
    Sentry.setUser(null);
    analytics.reset();
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check active session
    logAuthTransition("AuthProvider session bootstrap started", {
      path: window.location.pathname,
    });

    withSupabaseAuthRetry(() => supabase.auth.getSession(), {
      label: "auth_provider:getSession",
      metadata: {
        path: window.location.pathname,
      },
    })
      .then(({ data: { session } }) => {
        logAuthTransition("AuthProvider session bootstrap resolved", {
          path: window.location.pathname,
          has_session: !!session,
        });
        setSession(session);
        setUser(session?.user ?? null);
        setObservabilityUser(session?.user ?? null);
      })
      .catch((error) => {
        console.error("Failed to load auth session:", error);
        setSession(null);
        setUser(null);
        setObservabilityUser(null);
      })
      .finally(() => {
        setLoading(false);
      });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setObservabilityUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await withSupabaseAuthRetry(
      () =>
        supabase.auth.signInWithPassword({
          email,
          password,
        }),
      {
        label: "auth_provider:signInWithPassword",
        metadata: {
          path: window.location.pathname,
        },
      },
    );

    if (error) {
      throw error;
    }

    setSession(data.session);
    setUser(data.user);
    setObservabilityUser(data.user);
  };

  const signOut = async () => {
    const { error } = await withSupabaseAuthRetry(
      () => supabase.auth.signOut(),
      {
        label: "auth_provider:signOut",
        metadata: {
          path: window.location.pathname,
        },
      },
    );

    if (error) {
      throw error;
    }

    setSession(null);
    setUser(null);
    setObservabilityUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export { supabase };
